import { IMenuParameters, Popup, SearchControl, Tree } from '@Common/Controls';
import constants from '@Common/Core/Constant';
import { MenuSearchResult, SearchResultItem } from '@InstanceFactory';
import { IMenuItemSearch } from '@View/Components/Menus/MenuComponent';
import { MenuLevelStyle } from './MenuVisualizer';

enum SearchTextTemplate {
    SearchText = 'SearchText',
    Description = 'Description'
}

export class SearchMenuItemControl {
    private root: JQuery;
    private tree: Tree;
    private popup: Popup;
    private input: SearchControl;
    private results: MenuSearchResult[];
    private mask = '';
    private empty: JQuery;

    private readonly jointHeight = 30;
    private readonly popupWidth = 300;
    private readonly popupHeight = 300;

    constructor(private readonly params: IMenuItemSearch) {
        this.init();
    }

    private init() {
        const css: any = {
            padding: this.params.margin
        };
        if (this.params.rightmost) {
            css.position = 'absolute';
            css.right = 0;
        }
        const wrapper = $('<div class="menu-search-wrapper">').css(css);

        this.input = new SearchControl({
            className: `${this.params.borderStyleName} inherit-placeholder inverted`,
            css: this.params.textStyle,
            width: this.params.width,
            placeholder: strings.searchPlaceholder,
            clearIcon: this.params.searchClearImage,
            searchIcon: this.params.searchImage
        });

        let isSearchPending = false;
        const inputControl = this.input.getControl();
        inputControl
            .on('click', async () => {
                if (!this.results) {
                    this.results = await this.params.onLoadItems();
                    this.tree.items(this.getTreeItems());
                    isSearchPending && this.displayResults();
                } else {
                    this.displayResults();
                }
            })
            .on('keydown', e => {
                const code = e.keyCode ? e.keyCode : e.which;
                if (code === constants.keyCodes.UP_ARROW) {
                    e.preventDefault();
                    this.tree.selectPrevItem();
                }
                if (code === constants.keyCodes.DOWN_ARROW) {
                    e.preventDefault();
                    this.tree.selectNextItem();
                }
                if (code === constants.keyCodes.ENTER) {
                    e.preventDefault();
                    const selectedItems = this.tree.selectedItems();
                    if (selectedItems.length) {
                        this.params.onClick(selectedItems[0].data);
                        this.popup.hide();
                    }
                }
            });
        this.input.onChange(e => {
            if (this.results) {
                this.displayResults();
            } else {
                isSearchPending = true;
            }
        });
        wrapper.append(inputControl);

        this.root = $('<li class="search">').append(wrapper);

        const mls = new MenuLevelStyle();
        mls.itemDefaultStyle = mls.itemOpenedStyle = this.params.resultMenuItemStyleName;
        mls.itemSelectedStyle = mls.itemHoverStyle = this.params.resultMenuItemHoverStyleName;

        this.tree = new Tree({
            cloneItems: true,
            sortable: false,
            checkable: false,
            selectable: false,
            openByClick: false,
            width: this.popupWidth,
            height: this.popupHeight,
            contentStyle: 'no-expand-arrow component-menu-children',
            levelStyle: [mls],
            defaultLevelStyle: mls,
            possibleRightAlign: true,
            visibilityComparer: i => {
                return !this.params.checkIfDisabledByGroup(i.data.actionItem) &&
                    (
                        this.hasMaskInString(i.name) ||
                        (i.data.fullName && this.hasMaskInString(i.data.fullName)) ||
                        (i.data.tags && this.hasMaskInString(i.data.tags))
                    );
            }
        } as IMenuParameters);
        this.tree.onItemClick(item => {
            this.params.onClick(item.data);
            this.tree.selectedItems([]);
            this.popup.hide();
        });

        this.popup = new Popup({
            padding: 0,
            isHidden: true,
            jointHeight: this.jointHeight,
            css: this.params.popupBackground
        });

        this.empty = $('<div>')
            .css({ width: this.popupWidth })
            .addClass(this.params.resultMenuItemStyleName)
            .append(
                $(`<p>${strings.noDataString}</p>`).css({
                    margin: 0,
                    lineHeight: 3,
                    textAlign: 'center'
                })
            );
    }

    private getTreeItems(): any[] {
        return this.results.map(res => ({
            id: res.menuItemName,
            className: 'text-bold-important',
            name: res.menuItemName,
            data: {
                view: res.view,
                actionItem: res.actionItem
            },
            children: res.results.reduce((array, result, index) => {
                const processed = this.processTemplates(result);
                array.push({
                    id: `${res.menuItemName}-${index}`,
                    name: processed.name,
                    title: processed.title,
                    data: {
                        view: res.view,
                        componentId: result.componentId,
                        actionItem: res.actionItem,
                        fullName: processed.fullName,
                        tags: processed.tags
                    }
                });
                return array;
            }, []),
            expanded: true
        }));
    }

    private processTemplates(result: SearchResultItem): { name: string; fullName: string; title: string; tags: string; } {
        const { tags, template } = this.extractTags(result.quickSearchText);
        const text = this.processSingleTemplate(template, SearchTextTemplate.SearchText, result.title, false);
        const name = this.processSingleTemplate(text, SearchTextTemplate.Description, '');

        return {
            name,
            tags,
            fullName: this.processSingleTemplate(text, SearchTextTemplate.Description, result.description),
            title: (result.description || name).trim()
        };
    }

    private extractTags(text: string) {
        const tags = [];
        const template = text.replace(/##(\w+)/g, (match, group) => {
            tags.push(group);
            return '';
        });
        return {
            tags: tags.join(', '),
            template: this.replaceMultipleWhitespaces(template)
        };
    }

    private processSingleTemplate(source: string, template: SearchTextTemplate, value: string, removeBrackets = true): string {
        const result = source.replace(`[${template}]`, (value || '').trim());
        return removeBrackets ? this.removeEmptyBrackets(result) : result;
    }

    private removeEmptyBrackets(source: string): string {
        return source.replace(/\[([^[]*?)\]/g, (match, group) => {
            return (/\w/g).test(match) ? group : '';
        }).trim();
    }

    private replaceMultipleWhitespaces(source: string): string {
        return source.replace(/(\s)\1+/g, '$1').trim();
    }

    private hasMaskInString(str) {
        return str.toLowerCase().indexOf(this.mask.toLowerCase()) != -1;
    }

    private displayResults() {
        const value = this.input.getValue();

        this.mask = value;
        this.tree.setNodesVisibility();

        if (value) {
            const visibleNodes = this.tree.getVisibleNodes();
            const isEmpty = !visibleNodes.length;
            const parentItems = {};

            visibleNodes.forEach(node => {
                if (node.data.children) {
                    return;
                }

                let text = this.hasMaskInString(node.data.name) ? node.data.name : node.data.data.fullName;
                const textLength = text.length;
                const maskLength = this.mask.length;
                const charSpaceWidth = 10;

                if (textLength > maskLength + charSpaceWidth * 2) {
                    const index = text.toLowerCase().indexOf(this.mask.toLowerCase());
                    const lastIndex = text.lastIndexOf(' ', index - charSpaceWidth);
                    if (index != -1 && lastIndex != -1) {
                        text = '...' + text.substr(lastIndex + 1);
                    }
                }

                const values = parentItems[node.parent.data.id] || (parentItems[node.parent.data.id] = new Set());
                if (values.has(text)) {
                    node.visibility(false);
                } else {
                    values.add(text);
                    node.control.name.text(text);
                }
            });

            this.popup.setContent(isEmpty ? this.empty : this.tree.getControl());

            const { left, top } = this.input.getControl().offset();
            this.popup.show(left, top);

            const height = isEmpty ? this.empty.height() : Math.min(this.tree.getRealContentHeight(), this.popupHeight);
            this.popup.show(left, top, null, height);
        } else {
            this.popup.hide();
        }
    }

    public getControl(): JQuery {
        return this.root;
    }
}
